import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlreadyConnectedError, startConnection } from '@/services/integrations'
import { closeConnectWindow, navigateConnectWindow, openConnectWindow } from './connectPopup'
import { pollConnection } from './pollConnection'
import { confirmConnectionUsable } from './confirmConnectionUsable'
import { getActiveWorkspaceId } from '@/store/workspaceStore'

/**
 * Connect an app end to end: claim a popup, start OAuth, watch for the result.
 *
 * `mutate` must be called directly from a click handler — `openConnectWindow`
 * relies on the user gesture still being active.
 */
export function useConnectApp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ toolkitSlug, reconnect = false }) => {
      const workspaceId = getActiveWorkspaceId()
      if (!workspaceId) throw new Error('Select a workspace before connecting an app.')
      const popup = openConnectWindow()
      try {
        const started = await startConnection({ toolkitSlug, reconnect, workspaceId })
        if (!navigateConnectWindow(popup, started.redirect_url)) {
          throw new Error('The connection could not be started. Please try again.')
        }
        const settled = await pollConnection({ connectionId: started.connection_id, workspaceId })
        // ACTIVE means the token is valid, not that the account is usable.
        return confirmConnectionUsable(settled, started.connection_id, workspaceId)
      } catch (error) {
        closeConnectWindow(popup)
        if (error instanceof AlreadyConnectedError) {
          return { status: 'ACTIVE', timedOut: false, alreadyConnected: true }
        }
        throw error
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] })
    },
  })
}
